<script lang="ts">
    import { createEventDispatcher, onMount } from 'svelte/internal'
    import Fa from 'svelte-fa'
    import { faAngleLeft, faEdit, faPlusCircle, faSave, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
    import Zact from '../../_common/z/zact.svelte'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Zframe from '../../_common/z/zframe.svelte'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Content from '../../_common/content.svelte'
    import { ServiceCategoryEntity } from '../../../modules/posApi/service-categories/service-category.entity'
    import type { CreateServiceCategoryDto } from '../../../modules/posApi/service-categories/dto/create-service-category.dto'
    import type { UpdateServiceCategoryDto } from '../../../modules/posApi/service-categories/dto/update-service-category.dto'
    import { TaxParamEntity } from '../../../modules/posApi/tax-params/tax-param.entity'
    import ZContainer from '../../_common/z/z-container.svelte'
    import ZinputTitle from '../../_common/z/zinput-title.svelte'
    import Zinput from '../../_common/z/zinput.svelte'
    import type { BrasilApiCity } from '../../../modules/adapters/brasil-api.service'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import ZinputBreak from '../../_common/z/zinput-break.svelte'
    import { TaxParamNfseIssuerEnum, TaxParamStreetTypeEnum } from '../../../modules/posApi/tax-params/tax-param.enum'
    import ZinputEmpty from '../../_common/z/zinput-empty.svelte'
    import ZContainerCol from '../../_common/z/z-container-col.svelte'
    import ZinputSwitch from '../../_common/z/zinput-switch.svelte'
    import ZgridLine from '../../_common/z/zgrid_line.svelte'
    import { TaxInvoiceServiceIBSCBSInputDto } from '../../../modules/posApi/tax-invoices/tax-invoice-service.entity'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import { faFileImport } from '@fortawesome/free-solid-svg-icons'
    import { parseNfseXml } from '../../../modules/posApi/tax-params/nfse-xml-import'
    import type { NfseXmlImport } from '../../../modules/posApi/tax-params/nfse-xml-import'
    const dispatch = createEventDispatcher()

    // No emissor nacional a tela mostra só o que ele usa. Endereço completo, inscrição estadual e incentivo
    // fiscal eram da Tecnospeed: ficam num bloco próprio no fim da tela, visível para os outros emissores
    // (e, quando vier a NF-e, voltam no bloco dela). Estado e cidade ficam, porque dão o código IBGE.
    $: isNacional = entity?.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL

    function sendClose() {
        dispatch('close')
    }

    export let form: boolean = false

    let show = false
    let entity: TaxParamEntity = new TaxParamEntity()

    let cancellationOptions: { value: number; label: string }[] = []
    cancellationOptions.push({ label: '0 - Não informar motivo', value: 0 })
    cancellationOptions.push({ label: '1 - Erro na emissão', value: 1 })
    cancellationOptions.push({ label: '2 - Serviço não prestado', value: 2 })
    cancellationOptions.push({ label: '3 - Erro de assinatura', value: 3 })
    cancellationOptions.push({ label: '4 - Duplicidade da nota', value: 4 })
    cancellationOptions.push({ label: '5 - Erro de processamento', value: 5 })

    async function editEntity(id: string) {
        try {
            show = true
            entity = new TaxParamEntity()
            entity = await window.api.pos.taxParams.getOne(id)
            if (!entity) {
                newEntity()
            } else {
                if (!entity.nfseDefaultServiceIBSCBS) entity.nfseDefaultServiceIBSCBS = new TaxInvoiceServiceIBSCBSInputDto()
                const city = entity.city
                const ibge = entity.ibgeCode
                await loadCityOptions()
                entity.city = city
                entity.ibgeCode = ibge
            }
            if (entity.nfseProduction === null || entity.nfseProduction === undefined) entity.nfseProduction = true
            console.log('tax param', entity.nfseProduction, entity)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function newEntity() {
        entity = new TaxParamEntity()
        entity.nfseConfigIsNacional = false
        entity.nfseConfigSingleService = false
        if (!entity.nfseDefaultServiceIBSCBS) entity.nfseDefaultServiceIBSCBS = new TaxInvoiceServiceIBSCBSInputDto()
    }

    function adjustIssuerParams(dto: Partial<TaxParamEntity>) {
        if (dto.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL) {
            dto.nfseConfigIsNacional = true
            // a NFS-e nacional comporta um único serviço por nota (regra do leiaute): não é uma opção
            dto.nfseConfigSingleService = true
            // campos ocultos no nacional: as colunas não aceitam nulo, então vão vazios (ou falso) quando a
            // empresa nunca os preencheu. Quem já tinha valor mantém o que tinha.
            dto.streetType = dto.streetType ?? ('' as TaxParamStreetTypeEnum)
            dto.street = dto.street ?? ''
            dto.addressNumber = dto.addressNumber ?? ''
            dto.neighborhood = dto.neighborhood ?? ''
            dto.zipCode = dto.zipCode ?? ''
            dto.taxIncentive = dto.taxIncentive ?? false
        }
        return dto
    }

    // Alíquota dos tributos aproximados (Lei 12.741): obrigatória para ME/EPP do Simples no emissor nacional.
    // Sem ela a nota sai com R$ 0,00 de tributos, o que não reflete a carga real.
    function validateTotTrib(): boolean {
        const isSimplesMEEPP = entity.taxIsSimplesNacional && entity.taxRegime !== 5
        const isSet = (v: number) => v !== null && v !== undefined
        const informado =
            isSet(entity.nfseTotTribSN) || isSet(entity.nfseTotTribFed) || isSet(entity.nfseTotTribEst) || isSet(entity.nfseTotTribMun)
        if (entity.nfseActive && isNacional && isSimplesMEEPP && !informado) {
            window.error(
                'Informe o valor aproximado dos tributos em "Simples Nacional (%)", no bloco da NFSe: é a alíquota efetiva do DAS da empresa (consulte o contador).',
            )
            return false
        }
        return true
    }

    async function createEntity() {
        try {
            show = true

            let dto: Partial<TaxParamEntity> = {
                addressComplement: entity.addressComplement,
                addressNumber: entity.addressNumber,
                certFile: entity.certFile,
                certPassword: entity.certPassword,
                city: entity.city,
                companyLegalName: entity.companyLegalName,
                companyName: entity.companyName,
                document: entity.document,
                email: entity.email?.trim(),
                ibgeCode: entity.ibgeCode,
                logo: entity.logo,
                municipalReg: entity.municipalReg,
                neighborhood: entity.neighborhood,
                nfseActive: entity.nfseActive,
                nfseConfigIsNacional: entity.nfseConfigIsNacional,
                nfseConfigVersaoEsquema: entity.nfseConfigVersaoEsquema,
                nfseConfigMunicipalGrossRevenue: entity.nfseConfigMunicipalGrossRevenue,
                nfseConfigMunicipalInitialDate: entity.nfseConfigMunicipalInitialDate,
                nfseConfigMunicipalLaw: entity.nfseConfigMunicipalLaw,
                nfseConfigMunicipalLogin: entity.nfseConfigMunicipalLogin,
                nfseConfigMunicipalPasswrod: entity.nfseConfigMunicipalPasswrod,
                nfseConfigRPSNumberingNumber: entity.nfseConfigRPSNumberingNumber,
                nfseConfigRPSNumberingSeries: entity.nfseConfigRPSNumberingSeries,
                nfseCustomCodigoSubItemListaServico: entity.nfseCustomCodigoSubItemListaServico,
                nfseCustomNaturezaTributacao: entity.nfseCustomNaturezaTributacao,
                nfseCustomRegimeApuracaoTributaria: entity.nfseCustomRegimeApuracaoTributaria,
                nfseDefaultServiceCNAE: entity.nfseDefaultServiceCNAE,
                nfseDefaultServiceCode: entity.nfseDefaultServiceCode,
                nfseDefaultServiceTaxCode: entity.nfseDefaultServiceTaxCode,
                nfseDefaultServiceTaxRate: entity.nfseDefaultServiceTaxRate,
                nfseDefaultServiceTaxType: entity.nfseDefaultServiceTaxType,
                nfseDefaultServiceTaxSituation: entity.nfseDefaultServiceTaxSituation,
                nfseDefaultServiceISSRetido: entity.nfseDefaultServiceISSRetido,
                notes: entity.notes,
                phone: entity.phone,
                state: entity.state,
                stateReg: entity.stateReg,
                status: entity.status,
                street: entity.street,
                streetType: entity.streetType,
                taxIncentive: entity.taxIncentive,
                taxIsSimplesNacional: entity.taxIsSimplesNacional,
                taxRegime: entity.taxRegime,
                taxRegimeSpecial: entity.taxRegimeSpecial,
                zipCode: entity.zipCode,
                nfseConfigSingleService: entity.nfseConfigSingleService,
                nfseConfigSingleServiceDefaultDescription: entity.nfseConfigSingleServiceDefaultDescription,
                nfseDefaultCancellationReason: entity.nfseDefaultCancellationReason,
                nfseDefaultCancellationReasonMessage: entity.nfseDefaultCancellationReasonMessage,
                nfseProduction: entity.nfseProduction,
                nfseDefaultServiceNBS: entity.nfseDefaultServiceNBS,
                nfseDefaultServiceCodigoContribuinte: entity.nfseDefaultServiceCodigoContribuinte,
                nfseDefaultServiceCodigoCidadeIncidencia: entity.nfseDefaultServiceCodigoCidadeIncidencia,
                nfseDefaultServiceCodigoServicoNacional: entity.nfseDefaultServiceCodigoServicoNacional,
                nfseDefaultServiceIBSCBS: entity.nfseDefaultServiceIBSCBS,
                nfseTotTribSN: entity.nfseTotTribSN,
                nfseTotTribFed: entity.nfseTotTribFed,
                nfseTotTribEst: entity.nfseTotTribEst,
                nfseTotTribMun: entity.nfseTotTribMun,
                nfseIssuer: entity.nfseIssuer,
            }
            dto = adjustIssuerParams(dto)
            entity = await window.api.pos.taxParams.create(dto)
            camposRevisar = {}
            if (form) {
                sendClose()
                return
            }
            window.alert('Configurações criadas com sucesso.')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function updateEntity() {
        try {
            show = true
            let dto: Partial<TaxParamEntity> = {
                addressComplement: entity.addressComplement,
                addressNumber: entity.addressNumber,
                certFile: entity.certFile,
                certPassword: entity.certPassword,
                city: entity.city,
                companyLegalName: entity.companyLegalName,
                companyName: entity.companyName,
                document: entity.document,
                email: entity.email?.trim(),
                ibgeCode: entity.ibgeCode,
                logo: entity.logo,
                municipalReg: entity.municipalReg,
                neighborhood: entity.neighborhood,
                nfseActive: entity.nfseActive,
                nfseConfigIsNacional: entity.nfseConfigIsNacional,
                nfseConfigVersaoEsquema: entity.nfseConfigVersaoEsquema,
                nfseConfigMunicipalGrossRevenue: entity.nfseConfigMunicipalGrossRevenue,
                nfseConfigMunicipalInitialDate: entity.nfseConfigMunicipalInitialDate,
                nfseConfigMunicipalLaw: entity.nfseConfigMunicipalLaw,
                nfseConfigMunicipalLogin: entity.nfseConfigMunicipalLogin,
                nfseConfigMunicipalPasswrod: entity.nfseConfigMunicipalPasswrod,
                nfseConfigRPSNumberingNumber: entity.nfseConfigRPSNumberingNumber,
                nfseConfigRPSNumberingSeries: entity.nfseConfigRPSNumberingSeries,
                nfseCustomCodigoSubItemListaServico: entity.nfseCustomCodigoSubItemListaServico,
                nfseCustomNaturezaTributacao: entity.nfseCustomNaturezaTributacao,
                nfseCustomRegimeApuracaoTributaria: entity.nfseCustomRegimeApuracaoTributaria,
                nfseDefaultServiceCNAE: entity.nfseDefaultServiceCNAE,
                nfseDefaultServiceCode: entity.nfseDefaultServiceCode,
                nfseDefaultServiceTaxCode: entity.nfseDefaultServiceTaxCode,
                nfseDefaultServiceTaxRate: entity.nfseDefaultServiceTaxRate,
                nfseDefaultServiceTaxType: entity.nfseDefaultServiceTaxType,
                nfseDefaultServiceTaxSituation: entity.nfseDefaultServiceTaxSituation,
                nfseDefaultServiceISSRetido: entity.nfseDefaultServiceISSRetido,
                notes: entity.notes,
                phone: entity.phone,
                state: entity.state,
                stateReg: entity.stateReg,
                status: entity.status,
                street: entity.street,
                streetType: entity.streetType,
                taxIncentive: entity.taxIncentive,
                taxIsSimplesNacional: entity.taxIsSimplesNacional,
                taxRegime: entity.taxRegime,
                taxRegimeSpecial: entity.taxRegimeSpecial,
                zipCode: entity.zipCode,
                nfseConfigSingleService: entity.nfseConfigSingleService,
                nfseConfigSingleServiceDefaultDescription: entity.nfseConfigSingleServiceDefaultDescription,
                nfseDefaultCancellationReason: entity.nfseDefaultCancellationReason,
                nfseDefaultCancellationReasonMessage: entity.nfseDefaultCancellationReasonMessage,
                nfseProduction: entity.nfseProduction,
                nfseDefaultServiceNBS: entity.nfseDefaultServiceNBS,
                nfseDefaultServiceCodigoContribuinte: entity.nfseDefaultServiceCodigoContribuinte,
                nfseDefaultServiceCodigoCidadeIncidencia: entity.nfseDefaultServiceCodigoCidadeIncidencia,
                nfseDefaultServiceCodigoServicoNacional: entity.nfseDefaultServiceCodigoServicoNacional,
                nfseDefaultServiceIBSCBS: entity.nfseDefaultServiceIBSCBS,
                nfseTotTribSN: entity.nfseTotTribSN,
                nfseTotTribFed: entity.nfseTotTribFed,
                nfseTotTribEst: entity.nfseTotTribEst,
                nfseTotTribMun: entity.nfseTotTribMun,
                nfseIssuer: entity.nfseIssuer,
            }
            dto = adjustIssuerParams(dto)
            entity = await window.api.pos.taxParams.update(entity.taxParamId, dto)
            camposRevisar = {}
            if (form) {
                sendClose()
                return
            }
            window.alert('Configurações atualizadas com sucesso.')
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    async function saveEntity() {
        console.log('initial entity', entity)
        if (!validateTotTrib()) return
        if (entity.taxParamId) {
            updateEntity()
        } else {
            createEntity()
        }
    }

    onMount(async () => {
        await editEntity(window.store.company.companyId)
    })

    const states = [
        'AC', // Acre
        'AL', // Alagoas
        'AP', // Amapá
        'AM', // Amazonas
        'BA', // Bahia
        'CE', // Ceará
        'DF', // Distrito Federal
        'ES', // Espírito Santo
        'GO', // Goiás
        'MA', // Maranhão
        'MT', // Mato Grosso
        'MS', // Mato Grosso do Sul
        'MG', // Minas Gerais
        'PA', // Pará
        'PB', // Paraíba
        'PR', // Paraná
        'PE', // Pernambuco
        'PI', // Piauí
        'RJ', // Rio de Janeiro
        'RN', // Rio Grande do Norte
        'RS', // Rio Grande do Sul
        'RO', // Rondônia
        'RR', // Roraima
        'SC', // Santa Catarina
        'SP', // São Paulo
        'SE', // Sergipe
        'TO', // Tocantins
    ]
    let stateOptions = []
    for (const state of states) {
        stateOptions.push({
            value: state,
            label: state,
        })
    }

    let cityOptions = []
    let cities: BrasilApiCity[] = []

    async function loadCityOptions() {
        ibgeDisabled = true
        entity.ibgeCode = ''
        entity.city = ''
        show = true
        try {
            cities = await window.api.events.cities.getAll({ uf: entity.state }) // adapters.BrasilAPI.IBGEGetCitiesByUF(entity.state)
            cityOptions = []
            for (const city of cities) {
                cityOptions.push({
                    value: city.nome,
                    label: city.nome,
                })
            }
            cityOptions = cityOptions
            if (cities.length === 1) {
                entity.city = cities[0].nome
                getIBGECode()
            }
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let ibgeDisabled = true
    function getIBGECode() {
        try {
            ibgeDisabled = true
            entity.ibgeCode = cities.find((el) => el.nome === entity.city)?.codigo_ibge || ''
        } catch (error) {
            entity.ibgeCode = ''
            ibgeDisabled = false
        }
    }

    let streetTypeOptions = Object.entries(TaxParamStreetTypeEnum).map(([key, value]) => ({
        label: value,
        value: key,
    }))

    let regimeOptions = []
    regimeOptions.push({ label: '0 - Nenhum', value: 0 })
    regimeOptions.push({ label: '1 - Simples Nacional', value: 1 })
    regimeOptions.push({ label: '2 - Simples Nacional - Excesso', value: 2 })
    regimeOptions.push({ label: '3 - Normal - Lucro Presumido', value: 3 })
    regimeOptions.push({ label: '4 - Normal - Lucro Real', value: 4 })
    regimeOptions.push({ label: '5 - MEI', value: 5 })

    let regimeSpecialOptions = []
    regimeSpecialOptions.push({ label: '0 - Sem regime tributário especial', value: 0 })
    regimeSpecialOptions.push({ label: '1 - Micro Empresa Municipal', value: 1 })
    regimeSpecialOptions.push({ label: '2 - Estimativa', value: 2 })
    regimeSpecialOptions.push({ label: '3 - Sociedade de Profissionais', value: 3 })
    regimeSpecialOptions.push({ label: '4 - Cooperativa', value: 4 })
    regimeSpecialOptions.push({ label: '5 - Microempresário Individual - MEI', value: 5 })
    regimeSpecialOptions.push({ label: '6 - Microempresa ou Pequeno Porte - EPP', value: 6 })
    regimeSpecialOptions.push({ label: '7 - Lucro Real', value: 7 })
    regimeSpecialOptions.push({ label: '8 - Lucro Presumido', value: 8 })
    regimeSpecialOptions.push({ label: '9 - Tributação Normal', value: 9 })
    regimeSpecialOptions.push({ label: '10 - Simples Nacional - com excesso do sublimite', value: 10 })
    regimeSpecialOptions.push({ label: '11 - Empresa de Responsabilidade Limitada', value: 11 })

    let issRetOptions = []
    issRetOptions.push({ label: '1 - Não retido', value: 1 })
    issRetOptions.push({ label: '2 - Retido pelo tomador', value: 2 })
    issRetOptions.push({ label: '3 - Retido pelo intermediário', value: 3 })

    // no type="file" o Zinput usa "options" como padrões do accept nativo, não como escolhas
    const certificadoAceito = [{ value: '.pfx', label: 'Certificado PFX' }, { value: '.p12', label: 'Certificado P12' }]

    let issuer = []
    issuer.push({ label: 'Tecnospeed', value: 'TECNOSPEED' })
    issuer.push({ label: 'Nacional', value: 'NACIONAL' })

    // As listas acima seguem a numeração da Tecnospeed. O emissor nacional manda o valor direto para a DPS,
    // então ele precisa das tabelas oficiais do padrão nacional — com a lista da Tecnospeed, "6 - EPP"
    // chegava ao Sefin como 6 = Sociedade de Profissionais.
    const regimeSpecialNacionalOptions = [
        { label: '0 - Nenhum', value: 0 },
        { label: '1 - Ato Cooperado (Cooperativa)', value: 1 },
        { label: '2 - Estimativa', value: 2 },
        { label: '3 - Microempresa Municipal', value: 3 },
        { label: '4 - Notário ou Registrador', value: 4 },
        { label: '5 - Profissional Autônomo', value: 5 },
        { label: '6 - Sociedade de Profissionais', value: 6 },
        { label: '9 - Outros', value: 9 },
    ]
    const cancellationNacionalOptions = [
        { label: '1 - Erro na emissão', value: 1 },
        { label: '2 - Serviço não prestado', value: 2 },
        { label: '9 - Outros', value: 9 },
    ]

    // ---- importação do XML de uma NFS-e --------------------------------------------------------------
    // Autopreenche a tela a partir do XML de uma NFS-e já emitida pela empresa (padrão nacional). Não salva:
    // o usuário revisa e clica em Salvar.

    let xmlInput: HTMLInputElement

    // Campos que a importação pediu para revisar, destacados em verde (highlight do Zinput). O destaque não
    // some ao digitar — só numa nova importação ou quando a configuração é salva com sucesso.
    let camposRevisar: Record<string, boolean> = {}
    $: hl = (campo: string): '' | 'attention' => (camposRevisar[campo] ? 'attention' : '')

    function openXmlImport() {
        xmlInput.value = ''
        xmlInput.click()
    }

    async function onXmlSelected() {
        const file = xmlInput.files?.[0]
        if (!file) return
        try {
            await importNfseXml(await file.text())
        } catch (error) {
            window.error(error)
        }
    }

    const normalize = (s: string) =>
        (s || '')
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .trim()
            .toUpperCase()

    async function importNfseXml(xml: string) {
        const data: NfseXmlImport = parseNfseXml(xml)

        const docTela = (entity.document || '').replace(/\D/g, '')
        const docXml = (data.document || '').replace(/\D/g, '')
        if (docTela && docXml && docTela !== docXml) {
            const continuar = confirm(
                `Este XML é do CNPJ/CPF ${data.document}, diferente do informado na tela (${entity.document}). Preencher a tela com os dados dele mesmo assim?`,
            )
            if (!continuar) return
        }

        const revisar: string[] = []
        // campos a destacar na tela (highlight do Zinput), pela chave usada em hl()
        const marcar: Record<string, boolean> = {}
        if (docTela && docXml && docTela !== docXml) marcar.document = true
        try {
            show = true

            // empresa
            if (data.document) entity.document = data.document
            if (data.legalName) entity.companyLegalName = data.legalName
            // sempre em destaque: é a conferência de que o XML importado é mesmo da empresa
            revisar.push(`Razão social: "${data.legalName}". Confira se o XML é mesmo desta empresa.`)
            marcar.companyLegalName = true
            if (data.phone) entity.phone = data.phone
            if (data.email) entity.email = data.email
            // no nacional a inscrição costuma ir vazia: o Sefin rejeita quando não confere com o cadastro
            entity.municipalReg = data.municipalReg

            // estado e cidade: a lista de cidades é carregada pela UF e a cidade é escolhida pelo código IBGE
            // do XML (o nome pode vir sem acento). É a cidade escolhida que preenche o código IBGE da tela.
            if (data.state) {
                entity.state = data.state
                await loadCityOptions()
                show = true
                const city =
                    cities.find((el) => String(el.codigo_ibge) === data.cityIbgeCode) ||
                    cities.find((el) => normalize(el.nome) === normalize(data.cityName))
                if (city) {
                    entity.city = city.nome
                    getIBGECode()
                } else {
                    revisar.push(`Cidade: "${data.cityName}" (IBGE ${data.cityIbgeCode}) não encontrada na lista de ${data.state}. Escolha manualmente.`)
                    marcar.city = true
                }
                if (city && data.cityIbgeCode && String(entity.ibgeCode) !== data.cityIbgeCode) {
                    revisar.push(`Código IBGE: a cidade escolhida tem ${entity.ibgeCode}, mas o XML traz ${data.cityIbgeCode}.`)
                    marcar.city = true
                }
            }

            // nome fantasia não vem na NFS-e: busca no cadastro da Receita (BrasilAPI), só se estiver vazio
            if (!entity.companyName && docXml.length === 14) {
                try {
                    const info = await window.api.adapters.BrasilAPI.IBGEGetCNPJInfo(docXml)
                    entity.companyName = info?.nome_fantasia || info?.razao_social || ''
                } catch (error) {
                    // sem a consulta, o usuário preenche
                }
            }
            if (!entity.companyName) {
                revisar.push('Nome fantasia: não consta na NFS-e; preencha.')
                marcar.companyName = true
            }

            // regime
            if (data.opSimpNac === 2) {
                entity.taxIsSimplesNacional = true
                entity.taxRegime = 5
            } else if (data.opSimpNac === 3) {
                entity.taxIsSimplesNacional = true
                if (entity.taxRegime !== 1 && entity.taxRegime !== 2) entity.taxRegime = 1
            } else {
                entity.taxIsSimplesNacional = false
                if (entity.taxRegime !== 3 && entity.taxRegime !== 4) {
                    entity.taxRegime = null
                    revisar.push('Regime tributário: escolha Lucro Presumido ou Lucro Real (a NFS-e não informa qual).')
                    marcar.taxRegime = true
                }
            }
            entity.nfseCustomRegimeApuracaoTributaria = data.opSimpNac === 3 && data.regApTribSN ? String(data.regApTribSN) : ''
            entity.taxRegimeSpecial = data.regEspTrib ?? 0

            // NFS-e
            entity.nfseActive = true
            entity.nfseIssuer = TaxParamNfseIssuerEnum.NACIONAL
            entity.nfseConfigIsNacional = true
            entity.nfseConfigSingleService = true
            entity.nfseProduction = data.production
            if (data.codigoTributacaoNacional) entity.nfseDefaultServiceCodigoServicoNacional = data.codigoTributacaoNacional
            entity.nfseDefaultServiceTaxCode = data.codigoTributacaoMunicipal
            if (data.nbs) entity.nfseDefaultServiceNBS = data.nbs
            if (data.description) {
                entity.nfseConfigSingleServiceDefaultDescription = data.description
                revisar.push(`Descrição padrão do serviço: veio "${data.description}", a desta nota. Confira se serve para notas com vários serviços.`)
                marcar.nfseConfigSingleServiceDefaultDescription = true
            }
            if (data.tribISSQN) entity.nfseDefaultServiceTaxSituation = data.tribISSQN
            entity.nfseDefaultServiceISSRetido = data.tpRetISSQN === 2 ? 2 : 1
            if (data.aliquotaISSQN !== null) entity.nfseDefaultServiceTaxRate = data.aliquotaISSQN
            else if (entity.nfseDefaultServiceTaxRate === null || entity.nfseDefaultServiceTaxRate === undefined) entity.nfseDefaultServiceTaxRate = 0

            // IBS/CBS: sem o grupo no XML, mantém o que a tela já tinha
            const ibs = entity.nfseDefaultServiceIBSCBS || new TaxInvoiceServiceIBSCBSInputDto()
            if (!ibs.valores?.tributacao) ibs.valores = { tributacao: { cst: '', cct: '' } }
            if (data.ibscbs) {
                if (data.ibscbs.finNFSe !== null) ibs.finalidadeNFSe = data.ibscbs.finNFSe
                if (data.ibscbs.indFinal !== null) ibs.operacaoPessoal = data.ibscbs.indFinal
                if (data.ibscbs.cIndOp) ibs.codigoOperacao = data.ibscbs.cIndOp
                if (data.ibscbs.cst) ibs.valores.tributacao.cst = data.ibscbs.cst
                if (data.ibscbs.cClassTrib) ibs.valores.tributacao.cct = data.ibscbs.cClassTrib
            }
            // padrões combinados para o que a nota não trouxer: uso ou consumo pessoal, finalidade regular
            if (ibs.operacaoPessoal === null || ibs.operacaoPessoal === undefined) {
                ibs.operacaoPessoal = 1
                revisar.push('Operação de uso ou consumo pessoal: a nota não trouxe; assumido 1 (sim). Para clientes empresa, o comum é 0.')
                marcar.operacaoPessoal = true
            }
            if (ibs.finalidadeNFSe === null || ibs.finalidadeNFSe === undefined) ibs.finalidadeNFSe = 0
            entity.nfseDefaultServiceIBSCBS = ibs

            // tributos aproximados (Lei 12.741)
            if (data.totTribSN !== null) entity.nfseTotTribSN = data.totTribSN
            if (data.totTribFed !== null) entity.nfseTotTribFed = data.totTribFed
            if (data.totTribEst !== null) entity.nfseTotTribEst = data.totTribEst
            if (data.totTribMun !== null) entity.nfseTotTribMun = data.totTribMun
            const temTotTrib = [entity.nfseTotTribSN, entity.nfseTotTribFed, entity.nfseTotTribEst, entity.nfseTotTribMun].some(
                (v) => v !== null && v !== undefined,
            )
            if (!temTotTrib) {
                if (data.opSimpNac === 3) {
                    revisar.push('Valor aproximado dos tributos: a nota não trouxe; preencha "Simples Nacional (%)" com a alíquota efetiva do DAS (obrigatório).')
                    marcar.nfseTotTribSN = true
                } else {
                    revisar.push('Valor aproximado dos tributos: a nota não trouxe; preencha os percentuais por esfera.')
                    marcar.nfseTotTribFed = marcar.nfseTotTribEst = marcar.nfseTotTribMun = true
                }
            }

            // cancelamento: não consta na NFS-e; o padrão combinado vale sempre
            entity.nfseDefaultCancellationReason = 1
            entity.nfseDefaultCancellationReasonMessage = 'Erro na emissão'

            // série e número da DPS: a combinação série + número não pode se repetir para o mesmo CNPJ. Se a
            // nota veio de outro emissor (não do Zandu) e usou a mesma série que o Zandu vai usar, o último
            // número sobe para pelo menos o dela — e os dois campos ficam destacados, porque podem ter
            // existido notas depois desta nessa série. Série diferente não colide com esse emissor.
            const doZandu = /^ZanduPOS/i.test(data.dpsVerAplic)
            const serieZandu = (entity.nfseConfigRPSNumberingSeries || '').trim() || '1' // mesmo padrão do pos-api
            if (!doZandu && data.dpsSerie && parseInt(data.dpsSerie, 10) === parseInt(serieZandu, 10)) {
                if (data.dpsNumber && (entity.nfseConfigRPSNumberingNumber || 0) < data.dpsNumber) {
                    entity.nfseConfigRPSNumberingNumber = data.dpsNumber
                }
                revisar.push(
                    `Série da DPS: a nota de origem foi emitida por outro sistema na mesma série (${data.dpsSerie}), com o número ${data.dpsNumber}. ` +
                        'O último número utilizado foi ajustado para não repetir; confira se houve notas depois desta nessa série, ou use uma série própria para o Zandu.',
                )
                marcar.nfseConfigRPSNumberingSeries = marcar.nfseConfigRPSNumberingNumber = true
            }

            // o certificado nunca vem da nota: sem ele não há emissão
            if (!entity.certFile) {
                revisar.push('Certificado digital: envie o arquivo .pfx/.p12 e informe a senha.')
                marcar.certFile = marcar.certPassword = true
            }

            entity = entity
        } finally {
            show = false
            camposRevisar = marcar
        }

        window.alert(
            'Campos na cor verde precisam de sua atenção.\n\n' +
                'Tela preenchida com os dados da NFS-e' +
                (data.number ? ` nº ${data.number}` : '') +
                '. Nada foi salvo: revise e clique em Salvar.' +
                (revisar.length ? '\n\nConfira:\n- ' + revisar.join('\n- ') : '') +
                '\n\nNão vem da NFS-e: certificado digital e senha.',
        )
    }
</script>

<LoadSpinner {show}></LoadSpinner>
<Content footer={true}>
    <Zact>
        <div class="spacer" />
        <!-- preenche a tela a partir do XML de uma NFS-e da empresa; não salva -->
        <input class="d-none" type="file" accept=".xml,text/xml,application/xml" bind:this={xmlInput} on:change={onXmlSelected} />
        <Zbutton
            color="secondary"
            icon={faFileImport}
            label="Importar XML de NFS-e"
            title="Preencher a tela com os dados de uma NFS-e já emitida pela empresa (padrão nacional)"
            mobileHideLabel={true}
            on:click={openXmlImport}
        />
        <button class="btn btn-primary" on:click={saveEntity}><Fa icon={faSave} /> Salvar</button>
    </Zact>

    <Zframe title="Dados da Empresa" class="h-space">
        <ZContainer>
            <ZinputTitle title="Dados Básicos"></ZinputTitle>
            <Zinput autocomplete="off" label="Nome Fantasia" size={4} type="text" required={true} highlight={hl('companyName')} bind:value={entity.companyName}></Zinput>
            <Zinput label="Razão Social" size={4} type="text" required={true} highlight={hl('companyLegalName')} bind:value={entity.companyLegalName}></Zinput>
            <Zinput label="CNPJ" size={4} type="text" required={true} highlight={hl('document')} bind:value={entity.document}></Zinput>
            <Zinput autocomplete="off" label="E-Mail" size={6} type="text" required={true} bind:value={entity.email}></Zinput>
            <Zinput autocomplete="off" label="Telefone" size={6} type="text" required={true} bind:value={entity.phone}></Zinput>
            <Zinput
                label="Inscrição Municipal"
                required={false}
                size={6}
                type="text"
                bind:value={entity.municipalReg}
                help="Para NFSe Nacional, dependendo do município, este campo deve ficar em branco."
            ></Zinput>

            <ZinputTitle title="Município" label="A cidade define o código IBGE, usado pela NFSe."></ZinputTitle>
            <Zinput
                autocomplete="off"
                label="Estado"
                options={stateOptions}
                size={2}
                type="select"
                required={true}
                bind:value={entity.state}
                on:change={loadCityOptions}
            ></Zinput>
            <Zinput
                autocomplete="off"
                label="Cidade"
                options={cityOptions}
                size={8}
                type="select"
                required={true}
                highlight={hl('city')} bind:value={entity.city}
                on:change={getIBGECode}
            ></Zinput>
            <Zinput autocomplete="off" label="Código IBGE" size={2} type="text" disabled={ibgeDisabled} required={true} value={entity.ibgeCode}></Zinput>

            <ZinputTitle title="Dados Fiscais"></ZinputTitle>
            <ZinputSwitch label="Optante pelo Simples Nacional" size={12} bind:value={entity.taxIsSimplesNacional}></ZinputSwitch>
            <Zinput label="Regime Tributário" options={regimeOptions} size={6} type="select" required={true} highlight={hl('taxRegime')} bind:value={entity.taxRegime}></Zinput>
            <Zinput
                label="Regime Especial de Tributação"
                options={isNacional ? regimeSpecialNacionalOptions : regimeSpecialOptions}
                size={6}
                type="select"
                required={true}
                help={isNacional
                    ? 'Tabela oficial da NFS-e nacional (regEspTrib). A maioria das empresas usa 0 - Nenhum. Atenção: valores escolhidos na lista antiga (da Tecnospeed) têm outro significado aqui.'
                    : ''}
                bind:value={entity.taxRegimeSpecial}
            ></Zinput>
        </ZContainer>
    </Zframe>
    <Zframe title="Certificado Digital" class="h-space">
        <ZContainer>
            <!-- presign={false} de propósito: quem usa o certificado é o pos-api, que baixa a URL por HTTP
                 simples, sem token (ver nfse-nacional/cert/certificate.service.ts). Com link pré-assinado
                 a emissão quebraria. -->
            <Zinput
                size={6}
                sizeM={12}
                label="Certificado digital"
                type="file"
                presign={false}
                options={certificadoAceito}
                help="Arquivo .pfx ou .p12 emitido pela sua certificadora."
                highlight={hl('certFile')}
                bind:value={entity.certFile}
            />
            <Zinput label="Senha do certificado" size={6} type="text" highlight={hl('certPassword')} bind:value={entity.certPassword}></Zinput>
        </ZContainer>
    </Zframe>
    <Zframe title="NFSe - Nota de Serviços" class="h-space">
        <ZContainer>
            <ZinputSwitch label="Ativar NFSe" size={12} bind:value={entity.nfseActive}></ZinputSwitch>
            {#if entity.nfseActive}
                <Zinput label="Emissor da NFSe" options={issuer} size={3} type="select" required={true} bind:value={entity.nfseIssuer}></Zinput>
                <ZinputSwitch
                    label="Em produção"
                    help="Ao desativar o envio entra em modo de homologação para prefeituras compatíveis"
                    size={12}
                    bind:value={entity.nfseProduction}
                ></ZinputSwitch>
                {#if entity.nfseIssuer === TaxParamNfseIssuerEnum.TECNOSPEED}
                    <ZinputSwitch label="NFSe Nacional" size={12} bind:value={entity.nfseConfigIsNacional}></ZinputSwitch>
                    <Zinput
                        help="O usuário de acesso ao portal da NFSe da prefeitura."
                        label="Login da Prefeitura"
                        size={3}
                        type="text"
                        bind:value={entity.nfseConfigMunicipalLogin}
                    ></Zinput>
                    <Zinput
                        label="Senha da Prefeitura"
                        help="Senha de acesso ao portal da prefeitura."
                        size={3}
                        type="text"
                        bind:value={entity.nfseConfigMunicipalPasswrod}
                    ></Zinput>
                    <ZinputEmpty total={2}></ZinputEmpty>
                {/if}

                <ZinputTitle
                    title="Configurações Extras"
                    label="Algumas prefeituras podem exigir configurações extras. Caso ocorra um erro ao tentar emitir a nota por ausência de algum campo, você poderá informá-lo abaixo."
                ></ZinputTitle>
                {#if entity.nfseIssuer === TaxParamNfseIssuerEnum.TECNOSPEED}
                    <Zinput label="Receita bruta" size={4} type="number" bind:value={entity.nfseConfigMunicipalGrossRevenue}></Zinput>
                    <Zinput label="Lei" size={4} type="text" bind:value={entity.nfseConfigMunicipalLaw}></Zinput>
                    <Zinput label="Data de Início" size={4} type="date" bind:value={entity.nfseConfigMunicipalInitialDate}></Zinput>
                    <Zinput label="NaturezaTributação" size={4} type="text" bind:value={entity.nfseCustomNaturezaTributacao}></Zinput>
                    <Zinput label="CodigoSubItemListaServico" size={4} type="text" bind:value={entity.nfseCustomCodigoSubItemListaServico}></Zinput>
                    <Zinput
                        label="Versão do esquema"
                        size={4}
                        type="text"
                        help="Algumas prefeituras exigem a versão do esquema de NFSe. Ex.: RTC"
                        bind:value={entity.nfseConfigVersaoEsquema}
                    ></Zinput>
                {/if}

                <Zinput
                    label="Regime Apuração Tributária (regApTribSN)"
                    size={4}
                    type="text"
                    bind:value={entity.nfseCustomRegimeApuracaoTributaria}
                    help="Opções válidas: Vazio / 1 (ISSQN apurado no Simples) / 2 (ISSQN apurado no município) / 3 (ISSQN e tributos federais apurados fora do Simples)"
                ></Zinput>
                <Zinput
                    label={isNacional ? 'DPS - Série' : 'RPS - Série'}
                    help={isNacional
                        ? 'Série da DPS usada pelo Zandu. Não reutilize a série de outro emissor (como o portal do emissor nacional): a combinação série + número não pode se repetir.'
                        : 'Informe a série a ser utilizada para emissão do RPS.'}
                    size={4}
                    type="text"
                    highlight={hl('nfseConfigRPSNumberingSeries')}
                bind:value={entity.nfseConfigRPSNumberingSeries}
                ></Zinput>
                <Zinput
                    label={isNacional ? 'DPS - Último número utilizado' : 'RPS - Último número utilizado'}
                    help={isNacional
                        ? 'O Zandu usa o próximo número a cada emissão. Só altere se esta série já foi usada por outro emissor: nesse caso, informe o último número usado nela.'
                        : 'Algumas prefeituras darão erro caso um RPS seja emitido com número já utilizado ou caso deixe de utilizar um número sequencial.'}
                    size={4}
                    type="number"
                    highlight={hl('nfseConfigRPSNumberingNumber')}
                bind:value={entity.nfseConfigRPSNumberingNumber}
                ></Zinput>

                <Zinput
                    label="Código padrão para cancelamento"
                    size={4}
                    type="select"
                    options={isNacional ? cancellationNacionalOptions : cancellationOptions}
                    bind:value={entity.nfseDefaultCancellationReason}
                ></Zinput>
                <Zinput
                    label="Motivo padrão para cancelamento"
                    size={4}
                    type="text"
                    help="Algumas prefeituras exigem que seja informado um motivo para o cancelamento da NFSe. Ex.: Dados informados errados."
                    bind:value={entity.nfseDefaultCancellationReasonMessage}
                ></Zinput>

                <!-- <ZinputEmpty total={1}></ZinputEmpty> -->

                <ZinputTitle title="Configurações Padrão para Serviços" label="Parâmetros que vão em nível de serviços, e podem ser editadas ao emitir a NFSe."
                ></ZinputTitle>
                {#if entity.nfseIssuer === TaxParamNfseIssuerEnum.TECNOSPEED}
                    <Zinput
                        label="CNAE"
                        help="Código de atividade econômica ao qual o serviço se enquadra"
                        size={3}
                        type="text"
                        bind:value={entity.nfseDefaultServiceCNAE}
                    ></Zinput>

                    <Zinput
                        label="Código Item Lista Serviço"
                        required={true}
                        help="Código de serviço conhecido como Código Item Lista Serviço. Geralmente segue a LC116, podendo váriar de acordo com a prefeitura.\nPara NFSe Nacional esse valor deve conter 6 dígitos. Ex.: 060201"
                        size={3}
                        type="text"
                        bind:value={entity.nfseDefaultServiceCode}
                    ></Zinput>
                {/if}
                <Zinput
                    label="Código Tributação Nacional"
                    required={false}
                    size={3}
                    type="text"
                    bind:value={entity.nfseDefaultServiceCodigoServicoNacional}
                    help="Código de tributação nacional do serviço. Nacional: Para a estética geralmente é 060201."
                ></Zinput>

                <Zinput
                    label="Código de tributação municipal"
                    help="Tecnospeed: É obrigatório para alguns municípios. // Emissor nacional: pode ser em branco ou conter obrigatoriamente 3 dígitos; seu papel é de desambiguação do Código de Tributação Nacional dentro dos cadastros municipais."
                    size={3}
                    type="text"
                    bind:value={entity.nfseDefaultServiceTaxCode}
                ></Zinput>
                <Zinput
                    label="Alíquota do ISS"
                    required={true}
                    size={3}
                    type="number"
                    help={entity.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL
                        ? 'Alíquota do ISS em %. No emissor nacional só é enviada quando o ISS é retido pelo cliente, e nesse caso é obrigatória: é sobre ela que o cliente faz a retenção. Para empresa do Simples Nacional, informe a alíquota do ISS da faixa em que a empresa está (consulte o contador). Sem retenção, o próprio sistema nacional aplica a alíquota do município.'
                        : ''}
                    bind:value={entity.nfseDefaultServiceTaxRate}
                ></Zinput>
                <Zinput
                    label={entity.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL ? 'ISS Retido (padrão para clientes CNPJ)' : 'ISS Retido'}
                    required={false}
                    size={3}
                    type="select"
                    options={entity.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL ? issRetOptions.filter((el) => el.value !== 3) : issRetOptions}
                    bind:value={entity.nfseDefaultServiceISSRetido}
                    help={entity.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL
                        ? 'Padrão para notas de clientes pessoa jurídica (CNPJ), e pode ser trocado em cada nota. Cliente pessoa jurídica pode ser obrigado a reter o ISS, pela LC 116 ou pela lei do município dele. Para cliente pessoa física a nota vai sempre como não retido.'
                        : 'Selecione a opção de ISS Retido.'}
                ></Zinput>
                <Zinput
                    label="NBS"
                    required={false}
                    size={3}
                    type="text"
                    bind:value={entity.nfseDefaultServiceNBS}
                    help="Número NBS (geralmente 9 dígidos numéricos. Ex.: 1.2602.90.00) referente ao tipo de serviço conforme a tabela NBS."
                ></Zinput>
                {#if entity.nfseIssuer === TaxParamNfseIssuerEnum.TECNOSPEED}
                    <Zinput
                        label="Código Cidade Incidência"
                        required={false}
                        size={3}
                        type="text"
                        bind:value={entity.nfseDefaultServiceCodigoCidadeIncidencia}
                        help="Código na cidade de incidência (codigoCidadeIncidencia)."
                    ></Zinput>

                    <Zinput
                        label="Código Contribuinte"
                        required={false}
                        size={3}
                        type="text"
                        bind:value={entity.nfseDefaultServiceCodigoContribuinte}
                        help="Obrigatório para alguns padrões, como GOVBR30 (codigoContribuinte)."
                    ></Zinput>
                    <Zinput
                        label="Tipo de tributação (tipoTributacaoISSQN)"
                        required={false}
                        size={3}
                        type="number"
                        bind:value={entity.nfseDefaultServiceTaxType}
                    ></Zinput>
                {/if}
                <Zinput
                    help="Emissor Nacional: 1 (tributável), 2 (imune), 3 (exportação), 4 (não incidência) / Tecnspeed: vazio; Números de 0 até 10; Número 15. Mais informações: https://atendimento.tecnospeed.com.br/hc/pt-br/articles/15868001563415-Configura%C3%A7%C3%A3o-de-Situa%C3%A7%C3%A3o-Tribut%C3%A1ria-para-os-Padr%C3%B5es-IPM-e-IPM20"
                    label="Situação tributária (situacaoTributariaISSQN)"
                    required={false}
                    size={3}
                    type="number"
                    bind:value={entity.nfseDefaultServiceTaxSituation}
                ></Zinput>
                {#if entity.nfseIssuer === TaxParamNfseIssuerEnum.TECNOSPEED}
                    <ZContainerCol size={3}></ZContainerCol>
                {:else}
                    <ZContainerCol size={3}></ZContainerCol>
                    <ZContainerCol size={3}></ZContainerCol>
                {/if}

                <!-- no Nacional, um serviço por nota é regra do leiaute (sempre ligado, ver adjustIssuerParams) -->
                {#if entity.nfseIssuer !== TaxParamNfseIssuerEnum.NACIONAL}
                    <ZContainerCol size={3}>
                        <ZinputSwitch
                            label="Um serviço por nota"
                            help="Algumas prefeituras aceitam a inclusão de apenas um serviço por nota fiscal."
                            bind:value={entity.nfseConfigSingleService}
                        ></ZinputSwitch>
                    </ZContainerCol>
                {/if}
                <ZContainerCol size={3}>
                    <Zinput
                        label="Descrição padrão do serviço"
                        help={entity.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL
                            ? 'A NFS-e nacional comporta um único serviço por nota. Quando a nota tem um serviço só, vai a descrição dele; com mais de um, os valores são somados e vai esta descrição. Ex.: Atividades de estética'
                            : 'A descrição pode ser o nome do serviço ou ainda uma descrição breve do serviço prestado. Ex.: Atividades de estética'}
                        required={entity.nfseConfigSingleService || entity.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL}
                        type="text"
                        highlight={hl('nfseConfigSingleServiceDefaultDescription')} bind:value={entity.nfseConfigSingleServiceDefaultDescription}
                    ></Zinput>
                </ZContainerCol>
                <ZinputEmpty total={2}></ZinputEmpty>

                <ZinputTitle title="Configurações Padrão para Serviços - IBS / CBS" label="Parâmetros relativos ao IBS / CBS."></ZinputTitle>

                <Zinput
                    label="Finalidade NFSe"
                    help="0 (normal), 1 (complementar), 2 (ajuste), 3 (devolução)"
                    required={false}
                    size={3}
                    type="number"
                    bind:value={entity.nfseDefaultServiceIBSCBS.finalidadeNFSe}
                ></Zinput>
                <Zinput
                    label="Operação Pessoal (indFinal)"
                    help="0 (normal), 1 (uso ou consumo pessoal)"
                    required={false}
                    size={3}
                    type="number"
                    highlight={hl('operacaoPessoal')} bind:value={entity.nfseDefaultServiceIBSCBS.operacaoPessoal}
                ></Zinput>
                <Zinput
                    label="Código Indicador da Operação"
                    help="Código indicador da operação de fornecimento, conforme tabela 'código indicador de operação' (cIndOp). Estética geralmente é 030101"
                    required={false}
                    size={3}
                    type="text"
                    bind:value={entity.nfseDefaultServiceIBSCBS.codigoOperacao}
                ></Zinput>

                <Zinput
                    label="Valores > Tributação > CST"
                    help="Código de Situação Tributária do IBS/CBS: Valor comum: 000 (tributação integral). Tabela de referência: https://dfe-portal.svrs.rs.gov.br/DFE/ClassificacaoTributaria"
                    required={false}
                    size={3}
                    type="text"
                    bind:value={entity.nfseDefaultServiceIBSCBS.valores.tributacao.cst}
                ></Zinput>
                <Zinput
                    label="Valores > Tributação > CCT (cClassTrib)"
                    help="Código de Classificação de Tributação do IBS/CBS: Valor mais provável 000001. O valor 000001 significa Situações tributadas integralmente pelo IBS e CBS. Os 3 primeiros caracteres precisam ser obrigatoriamente iguais ao CST. Tabela de referência: https://dfe-portal.svrs.rs.gov.br/DFE/ClassificacaoTributaria"
                    required={false}
                    size={3}
                    type="text"
                    bind:value={entity.nfseDefaultServiceIBSCBS.valores.tributacao.cct}
                ></Zinput>

                {#if entity.nfseIssuer === TaxParamNfseIssuerEnum.NACIONAL}
                    <ZinputTitle
                        title="Valor Aproximado dos Tributos"
                        label="Valor aproximado dos tributos que incidem sobre o serviço, exigido pela Lei 12.741/2012 (Lei da Transparência) e regulamentado pelo Decreto 8.264/2014. Informe em percentual sobre o valor do serviço. Sem nenhum campo preenchido, a nota sai com os tributos zerados (R$ 0,00), o que não reflete a carga real: preencha ao menos um dos campos."
                    ></ZinputTitle>
                    <Zinput
                        label="Simples Nacional (%)"
                        help="Somente para empresas do Simples Nacional: informe a alíquota efetiva do DAS a que a empresa está sujeita, em %. Ex.: 6,54. Não é a alíquota nominal da tabela do anexo, e sim a efetiva, calculada a partir da receita bruta dos últimos 12 meses. Peça o valor ao contador e revise periodicamente, pois ela muda com o faturamento. O Decreto 8.264/2014 (art. 9º) permite que o Simples informe apenas essa alíquota. Para o MEI o preenchimento é opcional. Empresas fora do Simples deixam em branco e usam os campos por esfera."
                        required={false}
                        size={3}
                        type="number"
                        highlight={hl('nfseTotTribSN')} bind:value={entity.nfseTotTribSN}
                    ></Zinput>
                    <Zinput
                        label="Federal (%)"
                        help="Percentual aproximado dos tributos federais (PIS, COFINS, IRPJ, CSLL e outros) sobre o valor do serviço. Empresas do Simples Nacional não precisam preencher: basta o campo 'Simples Nacional (%)'. Fora do Simples, use a tabela IBPT do serviço ou o valor indicado pelo contador."
                        required={false}
                        size={3}
                        type="number"
                        highlight={hl('nfseTotTribFed')} bind:value={entity.nfseTotTribFed}
                    ></Zinput>
                    <Zinput
                        label="Estadual (%)"
                        help="Percentual aproximado dos tributos estaduais (ICMS) sobre o valor do serviço. Geralmente 0 para serviços. Empresas do Simples Nacional não precisam preencher: basta o campo 'Simples Nacional (%)'."
                        required={false}
                        size={3}
                        type="number"
                        highlight={hl('nfseTotTribEst')} bind:value={entity.nfseTotTribEst}
                    ></Zinput>
                    <Zinput
                        label="Municipal (%)"
                        help="Percentual aproximado dos tributos municipais (ISS) sobre o valor do serviço. Empresas do Simples Nacional não precisam preencher: basta o campo 'Simples Nacional (%)'. Fora do Simples, normalmente é a alíquota do ISS."
                        required={false}
                        size={3}
                        type="number"
                        highlight={hl('nfseTotTribMun')} bind:value={entity.nfseTotTribMun}
                    ></Zinput>
                {/if}
            {/if}
        </ZContainer>
    </Zframe>
    <!-- Dados que só a Tecnospeed usa. O emissor nacional não envia endereço nem inscrição estadual (o Sefin
         usa o cadastro nacional). Quando vier a NF-e, que exige o endereço do emitente, voltam no bloco dela. -->
    {#if !isNacional}
        <Zframe title="Dados complementares (Tecnospeed)" class="h-space">
            <ZContainer>
                <Zinput label="Inscrição Estadual" required={false} size={6} type="text" bind:value={entity.stateReg}></Zinput>
                <ZContainerCol size={6}>
                    <ZinputSwitch label="Possui incentivo fiscal" bind:value={entity.taxIncentive}></ZinputSwitch>
                </ZContainerCol>

                <ZinputTitle title="Endereço"></ZinputTitle>
                <Zinput
                    autocomplete="off"
                    label="Tipo de logradouro"
                    options={streetTypeOptions}
                    size={2}
                    type="select"
                    required={true}
                    bind:value={entity.streetType}
                ></Zinput>
                <Zinput autocomplete="off" label="Logradouro" size={4} type="text" required={true} bind:value={entity.street}></Zinput>
                <Zinput autocomplete="off" label="Número" size={3} type="text" required={true} bind:value={entity.addressNumber}></Zinput>
                <Zinput autocomplete="off" label="Complemento" size={3} type="text" bind:value={entity.addressComplement}></Zinput>

                <Zinput autocomplete="off" label="Bairro" required={true} size={3} type="text" bind:value={entity.neighborhood}></Zinput>
                <Zinput autocomplete="off" label="CEP" required={true} size={3} type="text" bind:value={entity.zipCode}></Zinput>
                <ZinputEmpty total={2}></ZinputEmpty>
            </ZContainer>
        </Zframe>
    {/if}
    <!-- <Zframe title="Material de ajuda" class="h-space"></Zframe> -->
</Content>

<style>
</style>
